// DOC-DEPS: LLM.md -> docs/ACTIVITY_LEXICON.md -> docs/ACTIVITY_MOOD_AUTO_RECOGNITION.md
//
// Chinese category lexicon – expands and replaces the sparse KEYWORDS object
// that previously lived in src/lib/activityType.ts.
//
// Design rule: every phrase present in activityLexicon.zh.ts MUST appear in
// at least one category below so that no recognised activity falls through to
// the 'life' default by lack of coverage.

import type { CategoryLexicon } from './types.js';

export const zhCategoryLexicon: CategoryLexicon = {
  keywords: {

    study: [
      // 核心学习词
      '学习', '复习', '预习', '课程', '上课', '听课', '下课', '读书',
      // 作业 / 题目
      '作业', '做作业', '做题', '刷题', '错题',
      // 考试备考
      '考试', '备考', '背单词', '背课文',
      // 阅读材料
      '看教材', '看论文', '读论文', '看书', '阅读文档', '读书',
      // 写作学术
      '写论文', '写作业', '写笔记', '做笔记', '查资料', '读书', '读文献', '读杂志',
      '写提纲', '做实验', '整理错题', '背书',
      // 英文
      'study', 'review', 'learn', 'lesson', 'homework', 'exam', 'quiz',
    ],

    work: [
      // 通用工作
      '工作', '上班', '办公', '开发',
      // 会议沟通
      '开会', '会议', '开组会', '组会', '开站会', '开视频会', '视频会议',
      '电话会议', '语音会议', '通电话', '接电话', '沟通', '复盘', '对齐',
      // 需求 / 项目
      '需求', '项目', '汇报', '做汇报', '准备汇报',
      // 写作输出
      '写周报', '写代码', '写方案', '改方案', '改代码', '写测试', '写用例',
      '写总结', '写文档', '做PPT', '写脚本', '写文章', '产出',
      '周报', '准备周报',
       '写报告', '改报告',
       '开评审会', '开需求会', '开复盘会', '写日报', '写月报',
       '整理会议纪要', '排期', '排班', '提测', '发版',
       // 操作型工作语义
       '查询日志', '查询数据', '检索资料', '搜索文档', '筛选记录', '过滤结果',
       '修改配置', '编辑文档', '更新资料', '更正文案',
       '提交工单', '提交申请', '提交代码', '提交审核',
       '身份认证', '二次验证',
       // 邮件消息
       '发邮件', '查邮件', '回邮件', '回消息',
      // 其他工作动作
      '竞品分析', 'review', '对接', '上线',
      // 英文
      'work', 'meeting', 'project', 'task', 'office', 'code', 'deploy',
      'standup', 'sprint', 'debug', 'coding',
    ],

    social: [
      // 聊天交流
      '聊天', '闲聊', '唠嗑', '连麦',
      // 社交活动
      '约会', '聚会', '聚餐', '约饭', '见朋友', '探亲', '串门', '拜访', '面基', '逛街',
      '逛商场', '逛便利店', '探店',
      // 家庭
      '陪娃', '带娃', '接娃', '送娃', '遛娃',
      // 人际关系词
      '朋友', '家人', '电话',
      // 英文
      'social', 'friend', 'family', 'chat', 'call', 'hangout', 'party', 'date',
    ],

    life: [
      // 饮食
      '吃饭', '吃早饭', '吃早餐', '吃午饭', '吃晚饭', '做饭', '买菜', '干饭',
      '喝咖啡', '喝水', '接水', '烧水', '烧开水', '烧饭', '煮饭', '蒸饭',
      '做菜', '烧菜', '炒菜', '切菜', '备菜', '淘米', '煮面', '下面',
      '洗菜', '择菜', '买米', '买面', '买牛奶', '买面包', '煮粥', '熬粥', '蒸菜', '焯水', '切水果', '削水果',
      '泡面', '冲奶粉', '泡燕麦', '洗锅', '刷锅', '收拾厨房', '清理冰箱', '囤菜', '囤货', '补货',
      '去买菜', '去超市采购', '采购食材', '做家常菜', '做汤',
      '煎蛋', '热饭', '热菜', '煲汤', '炖汤', '做早餐', '做午饭', '做晚饭', '喝茶', '泡牛奶',
      '点外卖', '拿外卖', '野餐',
      // 睡眠起居
      '睡觉', '午睡', '午休', '补觉', '起床', '洗漱', '刷牙', '洗澡',
      '护肤', '化妆', '洗头', '泡澡', '泡脚',
      '洗脸', '洗手', '上厕所',
      // 家务清洁
      '洗衣服', '晾衣服', '换床单', '收拾房间', '打扫卫生', '做家务', '清洁',
      '倒垃圾', '洗碗', '浇花', '浇菜',
      '拖地', '扫地', '擦桌子', '擦玻璃', '叠衣服',
      // 出行购物
      '出门', '打车', '开车', '骑车', '坐地铁', '坐公交', '通勤', '赶路',
      '加油', '充电', '洗车',
      '买东西', '买咖啡', '逛超市', '逛商场',
       '买早餐', '买午饭', '买晚饭', '买日用品', '买水果', '逛便利店',
       '取快递', '拿快递', '寄快递', '收快递', '拆快递',
       // 账号 / 订单等偏生活语义
       '查询订单', '查询快递', '登录账号', '退出登录', '重置密码', '绑定手机号', '解绑设备', '实名认证',
       // 宠物杂事
       '遛狗', '遛猫', '喂猫', '喂狗', '喂鱼', '铲屎',
      // 医疗
      '看医生', '看牙医', '体检', '拿药',
      // 修缮
      '修东西',
      // 记账
      '记账',
      // 英文
      'life', 'meal', 'commute', 'chores', 'clean', 'cook', 'grocery', 'laundry',
      'errand', 'shopping',
    ],

    entertainment: [
      // 游戏
      '打游戏', '玩游戏', '开黑', '聚众开黑', '桌游', '打牌',
      // 影视动漫
      '看电影', '看小说', '看动漫', '追番', '追剧', '看展', '看演出', '看直播',
      // 短视频音乐
      '刷短视频', '听歌', '听播客', '看视频', '刷手机', '看手机',
      // 娱乐活动
      'k歌', '唱歌', '摸鱼',
      // 阅读休闲
      '看报纸', '看新闻', '刷新闻',
      // 英文
      'entertainment', 'game', 'movie', 'music', 'video', 'relax',
      'gaming', 'anime', 'podcast', 'novel', 'series',
    ],

    health: [
      // 跑步 / 步行
      '跑步', '跑完步', '散步', '遛弯', '遛弯儿',
      // 健身
      '运动', '健身', '健身房', '撸铁',
      // 球类
      '打球', '打羽毛球', '打篮球', '踢足球', '打网球', '打乒乓球',
      // 其他体育
      '拉伸', '瑜伽', '跳绳', '跳操', '游泳', '骑行', '爬山', '徒步', '登山', '露营',
      // 医疗保健
      '看医生', '看牙医', '体检', '拿药', '挂号', '复诊',
      // 英文
      'health', 'exercise', 'gym', 'run', 'walk', 'yoga', 'swim', 'hike',
      'workout', 'sport', 'fitness', 'stretching', 'cycling', 'pilates',
    ],
  },
};
